import { Box, Button, ClickAwayListener, Paper, Popper, type SxProps, Typography } from '@mui/material';
import React, { type AriaAttributes, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ListBox } from './ListBox';
import { theme } from '../../theme';
type SingleSelectDropdownBaseProps = {
  labelPrefix?: string;
  hideOptionsLabel?: boolean;
  options: SingleSelectOption[];
  selectedOption?: string;
  onChange: (selected: string) => void;
  scrollBackToMainBar?: () => void;
  placeholder?: string;
  forceClose?: boolean;
};
type SingleSelectDropdownLabellingProps<TFilterOptions extends string> = {
  label: TFilterOptions;
  labelledById?: string;
} | {
  label?: TFilterOptions;
  labelledById: string;
};
type Props<TFilterOptions extends string> = SingleSelectDropdownBaseProps & SingleSelectDropdownLabellingProps<TFilterOptions>;
export type SingleSelectOption = {
  value: string;
  label: string;
  count?: number;
};
const styles = {
  button: {
    justifyContent: 'space-between',
    textAlign: 'left',
    borderRadius: '64px',
    py: 1,
    minHeight: '40px',
    px: 3,
    textTransform: 'none',
    '&:focus-visible': {
      outline: '2px solid',
      outlineColor: 'blues.light',
      cursor: 'pointer'
    }
  },
  buttonLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  listboxContainer: {
    minWidth: '200px',
    borderRadius: '8px',
    py: 2,
    my: 2,
    gap: 2
  },
  listboxLabelContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 3,
    py: 1,
    height: 36,
    width: '100%',
    bgcolor: 'white'
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ml: 2,
    color: 'neutral.dark',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.short
    })
  }
} as const satisfies Record<string, SxProps>;
export const SingleSelectDropdown = <TFilterOptions extends string,>({
  label,
  labelledById,
  labelPrefix = '',
  options,
  selectedOption,
  onChange,
  scrollBackToMainBar,
  placeholder,
  forceClose
}: Props<TFilterOptions>) => {
  const [isOpen, setIsOpen] = useState(false);
  const uniqueId = useId();
  const buttonId = `dropdown-button-${uniqueId}`;
  const optionsId = `dropdown-options-${uniqueId}`;
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (forceClose && isOpen) {
      setIsOpen(false);
    }
  }, [forceClose, isOpen]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isSelected = !!selectedOption;
  const [activeIndex, setActiveIndex] = React.useState(() => {
    const index = options.findIndex(option => option.value === selectedOption);
    return index === -1 ? 0 : index;
  });
  const handleToggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);
  const selectedOptionData = useMemo(() => options.find(option_0 => option_0.value === selectedOption), [options, selectedOption]);
  const displayText = `${labelPrefix}${selectedOptionData?.label}` || placeholder || label;
  const ariaProps: AriaAttributes = {};
  if (labelledById) {
    ariaProps['aria-labelledby'] = labelledById;
  } else if (label !== undefined) {
    ariaProps['aria-label'] = label;
  }
  return <Box ref={dropdownRef}>
      <Button id={buttonId} ref={buttonRef} onClick={handleToggleOpen} aria-haspopup="listbox" aria-expanded={isOpen} aria-controls={optionsId} role="combobox" variant="outlined" fullWidth sx={{
      ...styles.button,
      backgroundColor: isSelected ? 'greens.light' : 'white',
      borderWidth: isSelected ? '2px' : '1px',
      borderColor: isSelected ? 'primary.main' : 'neutral.lighter'
    }} {...ariaProps}>
        <Typography variant="textSmallBold" sx={{
        ...styles.buttonLabel,
        color: isSelected ? 'primary.main' : 'text.secondary'
      }}>
          {displayText}
        </Typography>
        <Box component="span" sx={{
        ...styles.iconContainer,
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
      }}>
          <Box component="span" className="mask-icon mask-icon-expand-more" aria-hidden="true" sx={{
          width: '24px',
          height: '24px',
          backgroundColor: isSelected ? 'primary.main' : 'neutral.dark'
        }} />
        </Box>
      </Button>
      <Popper open={isOpen} disablePortal popperOptions={{
      strategy: 'fixed'
    }} anchorEl={buttonRef.current} placement="bottom-start" sx={{
      zIndex: 200
    }}>
        <ClickAwayListener onClickAway={() => {
        setIsOpen(false);
        buttonRef.current?.focus();
      }}>
          <Paper square elevation={3} sx={styles.listboxContainer}>
            {!labelledById && <Box aria-hidden="true" sx={styles.listboxLabelContainer}>
                <Typography sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: 14
            }}>
                  {label}
                </Typography>
              </Box>}
            <ListBox id={uniqueId} isOpen={isOpen} closeDropdown={() => {
            setIsOpen(false);
            buttonRef.current?.focus();
          }} activeIndex={activeIndex} setActiveIndex={setActiveIndex} scrollBackToMainBar={scrollBackToMainBar} options={options} optionsId={optionsId} selectedOption={selectedOption} onChange={onChange} {...ariaProps} />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>;
};